class TabbedMediaCarousel {
  constructor(root) {
    this.root = root;
    this.currentIndex = 0;
    this.activeTabHandle = '';
    this.autoplayTimer = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.init();
  }

  init() {
    this.slides = Array.from(this.root.querySelectorAll('.tabbed-media-carousel__slide'));
    this.tabs = Array.from(this.root.querySelectorAll('.tabbed-media-carousel__tab'));
    this.thumbs = Array.from(this.root.querySelectorAll('.tabbed-media-carousel__thumb'));
    this.backdrops = Array.from(this.root.querySelectorAll('.tabbed-media-carousel__backdrop'));
    this.prevButton = this.root.querySelector('button[name="previous"]');
    this.nextButton = this.root.querySelector('button[name="next"]');
    this.viewport = this.root.querySelector('.tabbed-media-carousel__viewport');

    if (!this.slides.length) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.autoplayEnabled = this.root.dataset.autoplay === 'true';
    this.autoplaySpeed = (parseInt(this.root.dataset.speed, 10) || 5) * 1000;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectTab(tab.dataset.tabHandle));
    });

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const slideIndex = parseInt(thumb.dataset.slideIndex, 10);
        if (!Number.isNaN(slideIndex)) this.goToSlide(slideIndex);
      });
    });

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.stepSlide(-1));
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.stepSlide(1));
    }

    if (this.viewport) {
      this.viewport.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
      this.viewport.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
    }

    this.root.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.root.addEventListener('mouseleave', () => this.startAutoplay());
    this.root.addEventListener('focusin', () => this.pauseAutoplay());
    this.root.addEventListener('focusout', () => this.startAutoplay());

    const initialSlide = this.slides.find((slide) => slide.classList.contains('is-active')) || this.slides[0];
    const initialHandle = initialSlide?.dataset.tabHandle || '';

    if (this.tabs.length && initialHandle) {
      this.selectTab(initialHandle, { instant: true });
      this.startAutoplay();
    } else {
      this.activeTabHandle = initialHandle;
      this.applyFilters();
      this.syncUi();
      this.startAutoplay();
    }
  }

  getVisibleSlides() {
    return this.slides.filter((slide) => {
      if (slide.classList.contains('is-filtered')) return false;
      if (!this.tabs.length || !this.activeTabHandle) return true;
      return slide.dataset.tabHandle === this.activeTabHandle;
    });
  }

  applyFilters() {
    const hasTabs = this.tabs.length > 0;

    this.slides.forEach((slide) => {
      const matchesTab = !hasTabs || !this.activeTabHandle || slide.dataset.tabHandle === this.activeTabHandle;
      slide.classList.toggle('is-filtered', !matchesTab);
    });

    const visibleSlides = this.getVisibleSlides();
    if (!visibleSlides.length) return;

    const activeSlide = visibleSlides.find((slide) => slide.classList.contains('is-active'));
    if (!activeSlide) {
      this.slides.forEach((slide) => slide.classList.remove('is-active'));
      visibleSlides[0].classList.add('is-active');
      this.currentIndex = parseInt(visibleSlides[0].dataset.slideIndex, 10);
    }
  }

  selectTab(tabHandle, options = {}) {
    if (!tabHandle) return;
    this.activeTabHandle = tabHandle;
    this.applyFilters();

    const visibleSlides = this.getVisibleSlides();
    if (visibleSlides.length) {
      this.slides.forEach((slide) => slide.classList.remove('is-active'));
      visibleSlides[0].classList.add('is-active');
      this.currentIndex = parseInt(visibleSlides[0].dataset.slideIndex, 10);
    }

    this.syncUi();

    if (!options.instant) {
      this.startAutoplay();
    }
  }

  goToSlide(slideIndex) {
    const targetSlide = this.slides.find((slide) => parseInt(slide.dataset.slideIndex, 10) === slideIndex);
    if (!targetSlide || targetSlide.classList.contains('is-filtered')) return;

    this.slides.forEach((slide) => slide.classList.remove('is-active'));
    targetSlide.classList.add('is-active');
    this.currentIndex = slideIndex;
    this.syncUi();
    this.startAutoplay();
  }

  stepSlide(direction) {
    const visibleSlides = this.getVisibleSlides();
    if (visibleSlides.length < 2) return;

    const currentPosition = visibleSlides.findIndex(
      (slide) => parseInt(slide.dataset.slideIndex, 10) === this.currentIndex
    );
    const nextPosition = (currentPosition + direction + visibleSlides.length) % visibleSlides.length;
    const nextSlide = visibleSlides[nextPosition];
    this.goToSlide(parseInt(nextSlide.dataset.slideIndex, 10));
  }

  syncUi() {
    const activeSlide = this.slides.find((slide) => slide.classList.contains('is-active'));
    if (!activeSlide) return;

    const slideIndex = parseInt(activeSlide.dataset.slideIndex, 10);
    const tabHandle = activeSlide.dataset.tabHandle || '';
    const tabTextColor = activeSlide.dataset.tabTextColor || '#ffffff';
    const cardBorderColor = activeSlide.dataset.cardBorderColor || '#e32c2b';

    this.root.style.setProperty('--tmc-tab-text', tabTextColor);
    this.root.style.setProperty('--tmc-card-border-active', cardBorderColor);
    this.root.style.setProperty('--tmc-thumb-border-active', tabTextColor);

    const activeCard = activeSlide.querySelector('.tabbed-media-carousel__card');
    if (activeCard) {
      activeCard.style.setProperty('--tmc-card-border', cardBorderColor);
      activeCard.style.borderColor = cardBorderColor;
    }

    this.tabs.forEach((tab) => {
      tab.setAttribute('aria-selected', String(tab.dataset.tabHandle === tabHandle));
    });

    this.thumbs.forEach((thumb) => {
      const matchesTab = !this.tabs.length || thumb.dataset.tabHandle === tabHandle;
      thumb.classList.toggle('is-hidden', this.tabs.length > 0 && !matchesTab);
      thumb.setAttribute('aria-current', String(parseInt(thumb.dataset.slideIndex, 10) === slideIndex));
    });

    this.backdrops.forEach((backdrop) => {
      const isActive = parseInt(backdrop.dataset.slideIndex, 10) === slideIndex;
      backdrop.classList.toggle('is-active', isActive);
      if (isActive) this.preloadBackdrop(backdrop);
    });

    if (this.prevButton && this.nextButton) {
      const visibleCount = this.getVisibleSlides().length;
      const disabled = visibleCount < 2;
      this.prevButton.toggleAttribute('disabled', disabled);
      this.nextButton.toggleAttribute('disabled', disabled);
    }
  }

  startAutoplay() {
    this.pauseAutoplay();
    if (!this.autoplayEnabled || this.reducedMotion.matches) return;

    const visibleSlides = this.getVisibleSlides();
    if (visibleSlides.length < 2) return;

    this.autoplayTimer = window.setInterval(() => {
      this.stepSlide(1);
    }, this.autoplaySpeed);
  }

  pauseAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  preloadBackdrop(backdrop) {
    const image = backdrop?.querySelector('.tabbed-media-carousel__backdrop-img');
    if (!image || image.complete) return;
    image.loading = 'eager';
    if (image.currentSrc) {
      image.src = image.currentSrc;
    }
  }

  onTouchStart(event) {
    this.touchStartX = event.changedTouches[0].screenX;
  }

  onTouchEnd(event) {
    this.touchEndX = event.changedTouches[0].screenX;
    const delta = this.touchStartX - this.touchEndX;
    if (Math.abs(delta) < 40) return;
    this.stepSlide(delta > 0 ? 1 : -1);
  }
}

function initTabbedMediaCarousels(scope = document) {
  scope.querySelectorAll('[data-tabbed-media-carousel]').forEach((root) => {
    if (root.tabbedMediaCarousel) return;
    root.tabbedMediaCarousel = new TabbedMediaCarousel(root);
  });
}

document.addEventListener('DOMContentLoaded', () => initTabbedMediaCarousels());
document.addEventListener('shopify:section:load', (event) => initTabbedMediaCarousels(event.target));
